import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Download, Printer, Share2 } from 'lucide-react';
import { saveBlob, useApi, type PaperSize, type PosterTheme } from '@/lib/api';
import { Button, Card, ErrorText, PageHeader } from '@/components/ui';

/**
 * Campaign posters for a quiz: one page with a QR code to the quiz, for
 * notice boards (light, to print) and social media or mailing lists (dark).
 * Rendered on demand from the published quiz.
 */
export function QuizPosterPage() {
  const { slug = '' } = useParams();
  const api = useApi();
  const info = useQuery({
    queryKey: ['publicQuiz', slug],
    queryFn: () => api.publicQuiz(slug),
    retry: false,
  });

  if (info.isPending) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (info.isError) return <ErrorText error={info.error} />;

  return (
    <div className="max-w-4xl">
      <PageHeader
        title="Campaign poster"
        description={`A one-page poster for “${info.data.title}” with a QR code to the quiz. Print it for notice boards and common areas, or share the dark version online.`}
        back={{ to: `/q/${slug}`, label: 'Quiz' }}
      />
      <div className="grid gap-6 md:grid-cols-2">
        <PosterCard
          slug={slug}
          theme="light"
          title="For printing"
          hint="Light background that prints well in color or black and white."
          icon={<Printer className="size-4" aria-hidden />}
          downloads={[
            { label: 'US Letter PDF', format: 'pdf', size: 'letter' },
            { label: 'A4 PDF', format: 'pdf', size: 'a4' },
          ]}
        />
        <PosterCard
          slug={slug}
          theme="dark"
          title="For sharing"
          hint="Dark design for social media, newsletters and email."
          icon={<Share2 className="size-4" aria-hidden />}
          downloads={[
            { label: 'PNG image', format: 'png' },
            { label: 'PDF', format: 'pdf', size: 'letter' },
          ]}
        />
      </div>
    </div>
  );
}

interface PosterDownload {
  label: string;
  format: 'pdf' | 'png';
  size?: PaperSize;
}

function PosterCard({
  slug,
  theme,
  title,
  hint,
  icon,
  downloads,
}: {
  slug: string;
  theme: PosterTheme;
  title: string;
  hint: string;
  icon: React.ReactNode;
  downloads: PosterDownload[];
}) {
  const api = useApi();
  const preview = usePosterPreview(slug, theme);
  const download = useMutation({
    mutationFn: async (d: PosterDownload) => {
      const blob = await api.posterFile(slug, theme, d.format, d.size);
      const suffix = d.format === 'pdf' ? `-${d.size ?? 'letter'}` : '';
      saveBlob(blob, `Nirvik-Ready-Poster-${slug}-${theme}${suffix}.${d.format}`);
    },
  });

  return (
    <Card>
      <div className="flex items-center gap-2 font-medium">
        {icon}
        {title}
      </div>
      {/* Two lines' height on both cards, so the previews and buttons line up side by side. */}
      <p className="mt-1 text-sm text-muted-foreground md:min-h-10">{hint}</p>
      <div className="mt-4 aspect-[612/792] overflow-hidden rounded-md border border-border bg-muted">
        {preview.data ? (
          <img
            src={preview.data}
            alt={`${title} poster preview`}
            className="size-full object-contain"
          />
        ) : (
          <p className="p-4 text-sm text-muted-foreground">
            {preview.isError ? 'Preview unavailable.' : 'Preparing preview…'}
          </p>
        )}
      </div>
      <div className="mt-4 grid gap-2 sm:grid-cols-2">
        {downloads.map((d) => (
          <Button
            key={d.label}
            variant="secondary"
            disabled={download.isPending}
            onClick={() => download.mutate(d)}
          >
            <Download className="size-4" aria-hidden /> {d.label}
          </Button>
        ))}
      </div>
      <ErrorText error={download.error} />
    </Card>
  );
}

/** The PNG as a data: URL (the CSP allows data: images, not blob:). */
function usePosterPreview(slug: string, theme: PosterTheme) {
  const api = useApi();
  const png = useQuery({
    queryKey: ['poster', slug, theme],
    queryFn: () => api.posterFile(slug, theme, 'png'),
    staleTime: 5 * 60_000,
  });
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!png.data) return;
    const reader = new FileReader();
    reader.onload = () => setUrl(reader.result as string);
    reader.readAsDataURL(png.data);
  }, [png.data]);
  return { data: url, isError: png.isError };
}
